/** URL-backed grid filters shared between the inline header-row filters and
 *  the page toolbar (the project-wide grid filter rule — see
 *  aidocs/context/components.md).
 *
 *  The URL query string is the single source of truth, with two fallbacks
 *  underneath it — a loaded preset, and then the console-wide tenant scope:
 *
 *      URL ?tenant=   >   preset settings.filters.params.tenant   >   scope
 *
 *  That ordering is what makes a preset with a pinned tenant beat the scope
 *  while a preset without one combines with it. The scope is NEVER written into
 *  the tenant param: doing so would invert this precedence, trip
 *  `beforeWrite` into diverging every loaded preset, and make the URL
 *  non-pristine so saved default views would stop auto-loading.
 *
 *  Each `FilterDef` is one
 *  filter dimension; where a dimension has both a toolbar widget and a header
 *  filter they use the SAME `param`, so the two surfaces are always in sync —
 *  editing either writes the param, and both read it back.
 *
 *  Returns a controlled `filterModel` (drives the DataGridPro header row +
 *  filter panel), the resolved API `extra` params for the query, and
 *  `getParam`/`setParam`/`resetAll` for toolbar widgets. Extracted from the
 *  original hand-rolled sync in AuditLogPage so every grid behaves identically.
 */
import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TENANT_ALL } from '@/scope/scopeUrl';
import { formatTenantIds } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { useTenantScopeConsumer } from '@/scope/useTenantScopeConsumer';

export interface FilterDef {
  /** URL query param name. */
  param: string;
  /** API param name (defaults to `param`). */
  apiParam?: string;
  /** URL value → API value; return undefined to drop it (URLs are hand-editable). */
  toApi?: (v: string) => string | number | boolean | undefined;
  /** Grid column whose header-row filter edits this param (omit for toolbar-only). */
  column?: { field: string; operator: string };
  /** This def carries the tenant, so the console-wide scope layers in under it.
   *  Declared rather than inferred from the param name — three pages call it
   *  something else (`tour_operator_id`) or carry their own sentinel
   *  (`system`, `null`). At most one def per grid should set it.
   *
   *  - `true`: a one-tenant filter. It adopts the scope only when the scope names
   *    exactly one operator; a multi-operator scope leaves it alone (and the AppBar
   *    chip says this page works on one tenant at a time).
   *  - `'multi'`: a filter whose `toApi` takes a list (`tenantIdsToApi`). It adopts
   *    the whole scope as `'12,845'`. Flip a grid to `'multi'` together with its
   *    `toApi` and a `<TenantPicker multiple>`, never one without the others. */
  tenantScope?: boolean | 'multi';
}

export interface UseGridFilters {
  /** Controlled model for ResourceDataGrid (drives the header row + panel). */
  filterModel: GridFilterModel;
  onFilterModelChange: (m: GridFilterModel) => void;
  /** Resolved API params to spread into the list query. */
  extra: Record<string, string | number | boolean>;
  getParam: (param: string) => string | null;
  setParam: (param: string, value: string | null) => void;
  /** Atomically write several params (e.g. both ends of a date range). */
  setParams: (values: Record<string, string | null>) => void;
  resetAll: () => void;
  /** True when the PAGE itself has a filter set (URL or preset). The console
   *  scope deliberately does not count — it is ambient, has its own ✕ in the
   *  AppBar, and "Reset all filters" returns to it rather than clearing it. */
  anyActive: boolean;
  /** True when the tenant filter's value is coming from the console scope
   *  rather than from this page. */
  tenantFromScope: boolean;
}

/** Optional grid-presets integration (see useGridPresets). `presetParams`
 *  is a fallback read layer under the URL — a loaded preset supplies filter
 *  values without them appearing in the query string. `beforeWrite` runs
 *  first on every URL write so a preset-mode view can materialize its full
 *  state and drop `?preset` (the preset→custom divergence). */
export interface GridFiltersPresetLayer {
  presetParams: URLSearchParams | null;
  beforeWrite: (next: URLSearchParams) => void;
}

const modelFromUrl = (defs: FilterDef[], params: URLSearchParams): GridFilterModel => ({
  items: defs.flatMap((f) => {
    if (!f.column) return [];
    const value = params.get(f.param);
    return value
      ? [{ id: f.param, field: f.column.field, operator: f.column.operator, value }]
      : [];
  }),
});

/** Canonical {param: value} of the column-filter portion of a model / URL —
 *  compared to detect when the URL changed underneath the grid model. */
const columnParamsOfModel = (defs: FilterDef[], m: GridFilterModel): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const item of m.items) {
    const f = defs.find((c) => c.column?.field === item.field);
    const v = item.value as string | number | boolean | undefined;
    if (f && v !== undefined && v !== null && v !== '') out[f.param] = String(v);
  }
  return out;
};
const columnParamsOfUrl = (defs: FilterDef[], params: URLSearchParams): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const f of defs) {
    if (!f.column) continue;
    const v = params.get(f.param);
    if (v) out[f.param] = v;
  }
  return out;
};
const sameParams = (a: Record<string, string>, b: Record<string, string>) =>
  Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((k) => a[k] === b[k]);

export function useGridFilters(
  defs: FilterDef[],
  presetLayer?: GridFiltersPresetLayer,
): UseGridFilters {
  const [searchParams, setSearchParams] = useSearchParams();
  const presetParams = presetLayer?.presetParams ?? null;
  const beforeWrite = presetLayer?.beforeWrite;

  const scopeList = useTenantScopeList();
  const scopeDef = useMemo(() => defs.find((d) => d.tenantScope), [defs]);
  const multi = scopeDef?.tenantScope === 'multi';
  // Tell the AppBar chip that the scope has something to do on this page.
  useTenantScopeConsumer(scopeDef !== undefined, multi ? 'multi' : 'single');
  // What the scope contributes to this grid's tenant param: the whole list for a
  // multi filter, the one operator for a single one (a multi-operator scope does
  // not apply to a one-tenant filter), else nothing.
  const scope = !scopeDef
    ? null
    : multi
      ? formatTenantIds(scopeList.map((s) => s.id))
      : scopeList.length === 1
        ? String(scopeList[0]?.id)
        : null;

  // What the PAGE declares: the URL wins, a loaded preset fills the gaps.
  const pageParams = useMemo(() => {
    if (!presetParams) return searchParams;
    const merged = new URLSearchParams(presetParams);
    searchParams.forEach((v, k) => merged.set(k, v));
    return merged;
  }, [searchParams, presetParams]);

  // The console scope is the bottom layer — it only shows through where the
  // page said nothing about the tenant.
  const tenantFromScope =
    scope !== null && scopeDef !== undefined && !pageParams.get(scopeDef.param);
  const effectiveParams = useMemo(() => {
    if (!tenantFromScope || !scope || !scopeDef) return pageParams;
    const merged = new URLSearchParams(pageParams);
    merged.set(scopeDef.param, scope);
    return merged;
  }, [pageParams, tenantFromScope, scope, scopeDef]);

  /** Clearing the tenant filter while a scope is active cannot just delete the
   *  param — deleting it falls straight back to the scope layer, so the picker
   *  would snap back and be un-clearable. The sentinel says "all tenants HERE,
   *  despite the scope"; it is dropped before `toApi`, so the API sees no
   *  tenant filter at all. Only while the scope applies to this filter: a
   *  multi-operator scope under a one-tenant filter has nothing to fall back to. */
  const clearsToSentinel = (param: string) =>
    scope !== null && scopeDef !== undefined && param === scopeDef.param;

  const writeParam = (next: URLSearchParams, param: string, value: string | null) => {
    if (value) next.set(param, value);
    else if (clearsToSentinel(param)) next.set(param, TENANT_ALL);
    else next.delete(param);
  };

  const setParam = useCallback(
    (param: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          beforeWrite?.(next);
          writeParam(next, param, value);
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams, beforeWrite, scope, scopeDef],
  );

  const setParams = useCallback(
    (values: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          beforeWrite?.(next);
          for (const [k, v] of Object.entries(values)) writeParam(next, k, v);
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams, beforeWrite, scope, scopeDef],
  );

  const anyActive = defs.some((f) => pageParams.get(f.param));
  const resetAll = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        beforeWrite?.(next);
        for (const f of defs) next.delete(f.param);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams, defs, beforeWrite]);

  // Header-filter model: local (so an in-progress empty panel row survives),
  // seeded from the URL, folded from the URL on external (toolbar) changes.
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() =>
    modelFromUrl(defs, effectiveParams),
  );
  useEffect(() => {
    setFilterModel((prev) => {
      if (sameParams(columnParamsOfUrl(defs, effectiveParams), columnParamsOfModel(defs, prev))) {
        return prev;
      }
      const items: GridFilterItem[] = [];
      for (const f of defs) {
        const col = f.column;
        if (!col) continue;
        const value = effectiveParams.get(f.param);
        const prevItem = prev.items.find((i) => i.field === col.field);
        if (value) {
          items.push({
            id: prevItem?.id ?? f.param,
            field: col.field,
            operator: prevItem?.operator ?? col.operator,
            value,
          });
        } else if (
          prevItem &&
          (prevItem.value === undefined || prevItem.value === null || prevItem.value === '')
        ) {
          items.push(prevItem); // keep an in-progress empty panel row
        }
      }
      // Preserve the quick filter (toolbar search) — model-only, not a URL param.
      return { ...prev, items };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveParams, defs]);

  const onFilterModelChange = useCallback(
    (m: GridFilterModel) => {
      setFilterModel(m);
      // Only the column-backed items are URL state. A model change that leaves
      // them as they were — the quick filter (model-only, see above), an
      // in-progress empty panel row, or the grid re-emitting the model it was
      // handed — has nothing to write, and must not reach `beforeWrite`: that
      // would diverge a loaded preset over state presets don't even cover.
      if (sameParams(columnParamsOfModel(defs, m), columnParamsOfModel(defs, filterModel))) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          beforeWrite?.(next);
          // Same sentinel rule as setParam, in case a tenant def is ever
          // column-backed: an emptied header filter must not fall back to the
          // scope it was just cleared of.
          for (const f of defs) {
            if (!f.column) continue;
            if (clearsToSentinel(f.param)) next.set(f.param, TENANT_ALL);
            else next.delete(f.param);
          }
          for (const item of m.items) {
            const f = defs.find((c) => c.column?.field === item.field);
            const value = item.value as string | number | boolean | undefined;
            if (!f || value === undefined || value === null || value === '') continue;
            next.set(f.param, String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams, defs, beforeWrite, scope, scopeDef, filterModel],
  );

  const extra = useMemo(() => {
    const out: Record<string, string | number | boolean> = {};
    for (const f of defs) {
      const raw = effectiveParams.get(f.param);
      if (!raw) continue;
      // "all tenants here, despite the scope" — no API filter at all.
      if (f.tenantScope && raw === TENANT_ALL) continue;
      const value = f.toApi ? f.toApi(raw) : raw;
      if (value !== undefined) out[f.apiParam ?? f.param] = value;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveParams, defs]);

  return {
    filterModel,
    onFilterModelChange,
    extra,
    getParam: (param) => effectiveParams.get(param),
    setParam,
    setParams,
    resetAll,
    anyActive,
    tenantFromScope,
  };
}
